import { isMigratedCustomPageSlug } from '@shared/utils/migratedCustomPages';
import { matchPath, useLocationSelector } from '@shared/core';

const WEBSITE = 'website';
const PAGE = 'page';

export type CurrentPageSlug = Readonly<{
  currentPageSlug: string | undefined;
  isCustomPageSlug: boolean;
  isMigratedSlug: boolean;
  isUrlBasedCustomPageSlug: boolean;
}>;

/**
 * Pure counterpart of `useCurrentPageSlug`, for callers that already have a pathname.
 */
export function resolveCurrentPageSlug(pathname: string, eventHandle: string, isAdminPage?: boolean): CurrentPageSlug {
  const matchRoute = isAdminPage ? `/${eventHandle}/edit/${WEBSITE}/${PAGE}/:pageSlug` : `/${eventHandle}/${PAGE}/:pageSlug`;

  const routeMatch = matchPath<{ pageSlug: string }>(pathname, { path: `/${eventHandle}/:pageSlug` });
  const routeMatchCustomPage = matchPath<{ pageSlug: string }>(pathname, { path: matchRoute });

  const isUrlBasedCustomPageSlug = routeMatch?.params.pageSlug === PAGE;
  const rawSlug = isUrlBasedCustomPageSlug ? routeMatchCustomPage?.params.pageSlug : routeMatch?.params.pageSlug;
  const currentPageSlug = rawSlug?.split('?')[0] || undefined;

  const isMigratedSlug = !isAdminPage && isMigratedCustomPageSlug(currentPageSlug);

  const isCustomPageSlug = isUrlBasedCustomPageSlug || isMigratedSlug;

  return { currentPageSlug, isCustomPageSlug, isMigratedSlug, isUrlBasedCustomPageSlug };
}

const isSameCurrentPageSlug = (a: CurrentPageSlug, b: CurrentPageSlug) =>
  a.currentPageSlug === b.currentPageSlug &&
  a.isCustomPageSlug === b.isCustomPageSlug &&
  a.isMigratedSlug === b.isMigratedSlug &&
  a.isUrlBasedCustomPageSlug === b.isUrlBasedCustomPageSlug;

/**
 * To get current page path/slug. Re-renders the caller only when one of the returned fields
 * changes, not on every URL change (see useLocationSelector).
 * @param eventHandle - To get current page path/slug for event
 * @param isAdminPage - To get current page path/slug for admin / guest site
 */
export function useCurrentPageSlug(eventHandle: string, isAdminPage?: boolean): CurrentPageSlug {
  return useLocationSelector(location => resolveCurrentPageSlug(location.pathname, eventHandle, isAdminPage), isSameCurrentPageSlug);
}

/**
 * True while the URL is `/{eventHandle}/page/{pageSlug}` for this page — the admin-preview URL
 * that names one specific page (PRNT-4817). A plain boolean subscription, so a page widget that
 * only needs to know "is the URL pointed at me?" does not re-render as the guest scrolls through
 * other sections.
 */
export function useIsUrlRoutedToCustomPage(eventHandle: string, pageSlug: string): boolean {
  return useLocationSelector(location => {
    const { currentPageSlug, isUrlBasedCustomPageSlug } = resolveCurrentPageSlug(location.pathname, eventHandle);
    return isUrlBasedCustomPageSlug && !!currentPageSlug && currentPageSlug.toLowerCase() === pageSlug.toLowerCase();
  });
}
